
/**
 * w-features-benefits Section JavaScript
 * Handles scroll-based animation for the decoration line and item activation
 * Based on w-unique-products from sova-theme-europe
 */

class FeaturesBenefits {
  constructor(section) {
    this.section = section;
    this.container = section.querySelector('.w-features-benefits__content');
    this.decorationLine = section.querySelector('.w-features-benefits__decoration-line');
    this.items = Array.from(section.querySelectorAll('[data-feature-item]'));
    this.progressFill = null;

    if (!this.decorationLine || this.items.length === 0) {
      console.log('FeaturesBenefits: Missing elements', {
        decorationLine: !!this.decorationLine,
        itemsCount: this.items.length
      });
      return;
    }

    console.log('FeaturesBenefits initialized', {
      items: this.items.length,
      decorationLine: !!this.decorationLine
    });

    this.init();
  }

  init() {
    // Create progress fill element
    this.createProgressFill();

    // Calculate decoration line height
    this.calculateDecorationLineHeight();

    // Setup scroll listener for progress bar and items activation
    this.setupScrollListener();
  }

  createProgressFill() {
    // Create a fill element inside the decoration line
    this.progressFill = document.createElement('div');
    this.progressFill.className = 'w-features-benefits__decoration-line-fill';
    this.decorationLine.appendChild(this.progressFill);
    console.log('Progress fill created');
  }

  calculateDecorationLineHeight() {
    if (!this.decorationLine || this.items.length === 0) return;

    const firstItem = this.items[0];
    const lastItem = this.items[this.items.length - 1];

    if (!firstItem || !lastItem) return;

    // Get the first and last icon positions (more precise)
    const firstIcon = firstItem.querySelector('[data-feature-icon]');
    const lastIcon = lastItem.querySelector('[data-feature-icon]');

    if (!firstIcon || !lastIcon) {
      console.log('Icons not found');
      return;
    }

    // Calculate distance between first and last icon center
    const firstIconTop = firstIcon.offsetTop + (firstIcon.offsetHeight / 2);
    const lastIconTop = lastIcon.offsetTop + (lastIcon.offsetHeight / 2);

    // Height = distance between icon centers
    const calculatedHeight = lastIconTop - firstIconTop;

    // Update the decoration line height
    this.decorationLine.style.height = `${calculatedHeight}px`;
    console.log('Decoration line height calculated:', calculatedHeight + 'px', {
      firstIconTop,
      lastIconTop,
      itemsCount: this.items.length
    });
  }

  setupScrollListener() {
    let ticking = false;

    const updateProgress = () => {
      if (!this.container || this.items.length === 0) return;

      const firstItem = this.items[0];
      const lastItem = this.items[this.items.length - 1];

      if (!firstItem || !lastItem) return;

      const viewportHeight = window.innerHeight;
      const viewportCenter = viewportHeight / 2;
      const progressTrigger = viewportCenter;

      const firstItemRect = firstItem.getBoundingClientRect();
      const firstItemTop = firstItemRect.top;
      const lastItemRect = lastItem.getBoundingClientRect();
      const lastItemTop = lastItemRect.top;

      let scrollProgress = 0;

      // Calculate progress based on viewport center position
      if (firstItemTop <= progressTrigger && lastItemTop >= progressTrigger) {
        const totalDistance = firstItemTop - lastItemTop;
        const currentDistance = firstItemTop - progressTrigger;
        scrollProgress = Math.max(0, Math.min(1, currentDistance / totalDistance));
      } else if (firstItemTop > progressTrigger) {
        scrollProgress = 0;
      } else if (lastItemTop < progressTrigger) {
        scrollProgress = 1;
      }

      // Update progress fill height (percentage-based)
      const progressPercent = scrollProgress * 100;
      if (this.progressFill) {
        this.progressFill.style.height = `${progressPercent}%`;
      } else {
        console.warn('WARNING: progressFill element not found!');
      }

      // Update items states
      this.updateItemsStates(scrollProgress);

      ticking = false;
    };

    const requestUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(updateProgress);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });

    // Handle resize with debounce
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Recalculate line height on resize
        this.calculateDecorationLineHeight();
        // Update progress
        requestUpdate();
      }, 150);
    });

    // Initial update
    updateProgress();
  }

  updateItemsStates(scrollProgress) {
    if (this.items.length === 0 || !this.decorationLine) return;

    const firstItem = this.items[0];
    const firstIcon = firstItem?.querySelector('[data-feature-icon]');

    if (!firstItem || !firstIcon) return;

    // Get the total height of the decoration line
    const lineHeight = parseFloat(this.decorationLine.style.height) || 0;

    // Calculate actual progress bar height in pixels
    const progressBarHeight = lineHeight * scrollProgress;

    // Debug logs (uncomment to debug)
    // console.log('Progress update:', {
    //   scrollProgress: (scrollProgress * 100).toFixed(1) + '%',
    //   progressBarHeight: progressBarHeight.toFixed(1) + 'px',
    //   lineHeight: lineHeight.toFixed(1) + 'px'
    // });

    // Activate items based on progress bar position
    this.items.forEach((item, index) => {
      const icon = item.querySelector('[data-feature-icon]');
      if (!icon) return;

      // Calculate the position of each icon center relative to the first icon center
      const firstIconTop = firstIcon.offsetTop + (firstIcon.offsetHeight / 2);
      const iconTop = icon.offsetTop + (icon.offsetHeight / 2);
      const iconPosition = iconTop - firstIconTop;

      // Activate if the progress bar has reached this icon
      const isActive = progressBarHeight >= iconPosition;
      item.classList.toggle('is-active', isActive);
    });
  }
}

// Initialize all features-benefits sections
if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    const sections = document.querySelectorAll('.w-features-benefits');
    console.log('Found', sections.length, 'feature-benefits sections');
    sections.forEach(section => {
      new FeaturesBenefits(section);
    });
  });

  // Shopify section editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('.w-features-benefits');
      if (section) {
        new FeaturesBenefits(section);
      }
    });
  }
}

// Expose class globally for dynamic initialization
window.FeaturesBenefits = FeaturesBenefits;
